import React, { useState } from "react";
import {
    BaseEdge,
    EdgeLabelRenderer,
    getBezierPath,
    useReactFlow,
} from "reactflow";
import { Trash2 } from "lucide-react";

const CustomEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    style = {},
    markerEnd,
    data,
    selected,
    source,
    target,
    nodes,
    csrf_token,
}) => {
    const [showDeleteButton, setShowDeleteButton] = useState(false);
    const { deleteElements } = useReactFlow();

    // Listen for hide delete buttons event
    React.useEffect(() => {
        const handleHideDeleteButtons = () => {
            setShowDeleteButton(false);
        };

        window.addEventListener(
            "hideEdgeDeleteButtons",
            handleHideDeleteButtons
        );

        return () => {
            window.removeEventListener(
                "hideEdgeDeleteButtons",
                handleHideDeleteButtons
            );
        };
    }, []);

    const [edgePath, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    // Check if this edge is from a trigger node and if it has active triggers
    const isFromTriggerNode =
        source === "start-trigger" || source.startsWith("trigger-");
    const sourceNode =
        data?.nodes?.find((n) => n.id === source) ||
        nodes?.find((n) => n.id === source);
    const hasActiveTriggers =
        sourceNode?.type === "triggerNode" &&
        sourceNode.data?.enabledTriggers &&
        sourceNode.data.enabledTriggers.length > 0;

    // Determine edge styling based on source node type and validity
    const getEdgeStyle = () => {
        // Check source node type
        const sourceNodeType = sourceNode?.type;

        if (isFromTriggerNode) {
            if (hasActiveTriggers) {
                // Valid trigger connection - bright pink, enhanced when selected
                return {
                    stroke: selected ? "#ec4899" : "#f472b6", // bright pink
                    strokeWidth: selected ? 4 : 2,
                    strokeDasharray: "none",
                    opacity: selected ? 1 : 0.9,
                };
            } else {
                // Invalid trigger connection (no active triggers) - keep same red dashed style
                return {
                    stroke: selected ? "#dc2626" : "#ef4444",
                    strokeWidth: selected ? 4 : 2,
                    strokeDasharray: "5,5",
                    opacity: selected ? 1 : 0.7,
                };
            }
        } else if (sourceNodeType === "taskNode") {
            // Edge from task node - green
            return {
                stroke: selected ? "#059669" : "#10b981", // green
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "requestNode") {
            // Edge from request node - blue
            return {
                stroke: selected ? "#2563eb" : "#3b82f6", // blue
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "formNode") {
            // Edge from form node - orange
            return {
                stroke: selected ? "#f59e0b" : "#fbbf24", // orange
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "waitNode") {
            // Edge from wait node - match wait node orange
            return {
                stroke: selected ? "#d97706" : "#f59e0b",
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "ifElseNode") {
            // Edge from form node - orange
            return {
                stroke: selected ? "#8b5cf6" : "#8b5cf6", // green
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "updateNoteNode") {
            // Edge from update note node - sky blue
            return {
                stroke: selected ? "#0284c7" : "#0ea5e9",
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "updateJobNode") {
            return {
                stroke: selected ? "#4338ca" : "#6366f1",
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "createNoteNode") {
            // Edge from create note node - emerald
            return {
                stroke: selected ? "#059669" : "#10b981",
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else if (sourceNodeType === "sendTextNode") {
            return {
                stroke: selected ? "#db2777" : "#ec4899",
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        } else {
            // Default edge (fallback)
            return {
                stroke: selected ? "#2563eb" : "#64748b",
                strokeWidth: selected ? 3 : 2,
                strokeDasharray: "none",
            };
        }
    };

    // Show delete button for any selected edge.
    React.useEffect(() => {
        setShowDeleteButton(Boolean(selected));
    }, [selected]);

    const handleDeleteClick = async (e) => {
        e.stopPropagation();

        try {
            await deleteElements({ edges: [{ id }] });
            setShowDeleteButton(false);
        } catch (error) {
            console.error("Failed to delete edge:", error);
        }
    };

    return (
        <>
            <BaseEdge
                path={edgePath}
                markerEnd={markerEnd}
                style={getEdgeStyle()}
            />

            {/* Clickable overlay for edge interaction on all edges */}
            {/* This overlay makes thin edges easier to select */}
            {(
                <path
                    d={edgePath}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={20}
                    style={{ cursor: "pointer", pointerEvents: "all" }}
                    className="nodrag nopan"
                    onClick={(e) => {
                        // Don't stop propagation - let ReactFlow's onEdgeClick handle it
                        // The overlay just makes the edge easier to click
                    }}
                />
            )}

            {/* Delete button that appears when edge is clicked */}
            {showDeleteButton && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: "absolute",
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            pointerEvents: "all",
                        }}
                        className="nodrag nopan"
                    >
                        <button
                            onClick={handleDeleteClick}
                            style={{
                                background: "#dc2626",
                                border: "none",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = "#b91c1c";
                                e.target.style.transform = "scale(1.1)";
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = "#dc2626";
                                e.target.style.transform = "scale(1)";
                            }}
                            title="Delete connection"
                        >
                            <Trash2 size={12} color="white" />
                        </button>
                    </div>
                </EdgeLabelRenderer>
            )}

            {/* Removed clickable area - ReactFlow handles edge clicks directly */}

            {isFromTriggerNode && !hasActiveTriggers && !showDeleteButton && (
                <EdgeLabelRenderer>
                    <div
                        style={{
                            position: "absolute",
                            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
                            fontSize: 12,
                            pointerEvents: "all",
                        }}
                        className="nodrag nopan"
                    >
                        <div
                            style={{
                                background: "#fef2f2",
                                border: "1px solid #fecaca",
                                borderRadius: "4px",
                                padding: "2px 6px",
                                color: "#dc2626",
                                whiteSpace: "nowrap",
                                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            }}
                        >
                            ⚠️ No active triggers
                        </div>
                    </div>
                </EdgeLabelRenderer>
            )}
        </>
    );
};

export default CustomEdge;
